import type { ClaudeCodeNodeDescriptor } from './claude'
import type { CopilotCliNodeDescriptor } from './copilot'

export type WorkflowDefinitionScope = 'System' | 'Project'

export type TriggerInputType =
  | 'text'
  | 'textarea'
  | 'secret'
  | 'boolean'
  | 'integer'
  | 'number'
  | 'select'
  | 'context-picker'
  | 'connector-picker'
  | 'file-upload'
  | 'testsuite-picker'
  | 'repo-branch-map'
  | 'repo-file-picker'

export interface TriggerInputField {
  name: string
  label?: string
  type: TriggerInputType | string
  required: boolean
  defaultValue?: string | number | boolean
  default?: string | null
  description?: string | null
  allowedValues?: string[]
  options?: string[] | null
  placeholder?: string
  multiSelect?: boolean
  contextType?: string | null
  connectorType?: string | null
  folder?: string | null
  defer?: boolean
  accept?: string | null
  maxFileSizeBytes?: number | null
  maxTotalSizeBytes?: number | null
  visibleIf?: {
    field: string
    anyOf?: string[]
    noneOf?: string[]
  } | null
  dependsOn?: string | null
  hidden?: boolean
  baseBranchMapField?: string | null
  baseBranchRepoField?: string | null
  branchNameInfo?: boolean
}

export interface WorkflowTriggerSchema {
  workflowName?: string
  displayName?: string
  description?: string | null
  fields: TriggerInputField[]
  connectorRefs?: ConnectorRefSummary[]
  connectors?: TriggerConnectorRef[]
  contextRefs?: ContextRefSummary[]
  claudeCodeNodes?: ClaudeCodeNodeDescriptor[]
  baselineClaudeModel?: string
  baselineClaudeEffort?: string
  copilotCliNodes?: CopilotCliNodeDescriptor[]
  baselineCopilotModel?: string
  baselineCopilotEffort?: string
  availableAgentProviders?: string[]
  titanModel?: string | null
  resolvedSandboxImageId?: string | null
  resolvedSandboxImageName?: string | null
}

export interface ConnectorRefSummary {
  name: string
  connectorType: string
  isConnected: boolean
}

export interface ContextRefSummary {
  name: string
  contextSourceId: string | null
}

export interface WorkflowYamlSnapshot {
  id: string
  workflowDefinitionId: string
  yamlContent: string
  contentHash: string
  version: string
  createdAt: string
}

export interface WorkflowDefinition {
  id: string
  projectId: string | null
  name: string
  displayName: string
  description: string | null
  version: string
  yamlContent: string
  contentHash: string
  isActive: boolean
  isSystem: boolean
  permittedRoleIds: string[]
  applicableStatuses: string[]
  sandboxImageId: string | null
  linkedEntityScope?: LinkedEntityScope
  category?: WorkflowDefinitionCategory
  currentYamlSnapshotId: string | null
}

export type LinkedEntityScope = 'WorkItem' | 'Intent' | 'Project'

export const LINKED_ENTITY_SCOPES: LinkedEntityScope[] = ['WorkItem', 'Intent', 'Project']

export interface WorkflowDefinitionSummary {
  id: string
  projectId: string | null
  name: string
  displayName: string
  description: string | null
  version: string
  isActive: boolean
  isSystem: boolean
  yamlContent: string
  permittedRoleIds: string[]
  applicableStatuses: string[]
  linkedEntityScope?: LinkedEntityScope
  category?: WorkflowDefinitionCategory
}

export interface CreateWorkflowDefinitionRequest {
  name: string
  displayName: string
  description?: string
  version: string
  yamlContent: string
  isActive: boolean
  permittedRoleIds: string[]
  category?: WorkflowDefinitionCategory
  linkedEntityScope?: LinkedEntityScope
}

export interface UpdateWorkflowDefinitionRequest {
  name: string
  displayName: string
  description?: string
  version: string
  yamlContent: string
  isActive: boolean
  permittedRoleIds: string[]
  category?: WorkflowDefinitionCategory
  linkedEntityScope?: LinkedEntityScope
}

export type WorkflowDefinitionCategory = 'Analysis' | 'Implementation' | 'Testing' | 'Documentation' | 'DatabaseMigration'

export const WORKFLOW_DEFINITION_CATEGORIES: WorkflowDefinitionCategory[] = [
  'Analysis',
  'Implementation',
  'Testing',
  'Documentation',
  'DatabaseMigration',
]

export const CATEGORY_DOT_COLORS: Record<WorkflowDefinitionCategory | 'Uncategorized', string> = {
  Analysis: 'bg-info-foreground',
  Implementation: 'bg-success-foreground',
  Testing: 'bg-purple-foreground',
  Documentation: 'bg-warning-foreground',
  DatabaseMigration: 'bg-button',
  Uncategorized: 'bg-neutral-foreground',
}

export const CATEGORY_LABELS: Record<WorkflowDefinitionCategory | 'Uncategorized', string> = {
  Analysis: 'Analysis',
  Implementation: 'Implementation',
  Testing: 'Testing',
  Documentation: 'Documentation',
  DatabaseMigration: 'Database Migration',
  Uncategorized: 'Uncategorized',
}

export type NodeTypeCategory = 'AI' | 'Development' | 'Integration' | 'Utilities'

export interface NodeTypeDescriptor {
  typeName: string
  displayName: string
  description: string
  executionMode: string
  category: NodeTypeCategory
  isActive: boolean
  container: ContainerDescriptor | null
  inputs: NodeInputDescriptor[]
  expectedOutputs: NodeOutputDescriptor[]
  yamlSnippet: string
}

export interface ContainerDescriptor {
  requiredEnvironmentVars: string[]
  entrypoint: string | null
}

export interface NodeInputDescriptor {
  name: string
  description: string
  isRequired: boolean
}

export interface NodeOutputDescriptor {
  name: string
  description: string
}

export interface WorkflowValidationResult {
  isValid: boolean
  errors: WorkflowValidationError[]
  warnings: WorkflowValidationWarning[]
}

export interface WorkflowValidationError {
  message: string
  layer: string
  lineNumber: number | null
}

export interface WorkflowValidationWarning {
  message: string
  lineNumber: number | null
}

export const KEBAB_CASE_REGEX = /^[a-z0-9]+(-[a-z0-9]+)*$/

export interface WorkflowDefinitionFilters {
  search: string
  status: 'all' | 'active' | 'inactive'
  type: 'all' | 'system' | 'custom'
  category: 'all' | WorkflowDefinitionCategory
}

export interface WorkflowTaskParameterSchema {
  name: string
  displayName?: string
  type: 'string' | 'integer' | 'boolean' | 'secret' | 'text' | 'textarea' | 'select'
  required: boolean
  description: string
  defaultValue?: string | number | boolean
  options?: string[]
}

export interface ChatCommandDescriptor {
  name: string
  displayName: string
  description: string
  category: NodeTypeCategory
  icon: string
  requiresContainer: boolean
  parameters: WorkflowTaskParameterSchema[]
}

export interface ChatCommandResult {
  success: boolean
  outputs: Record<string, string>
  errorMessage: string | null
  durationMs: number
}

export interface TriggerWorkflowResponse {
  workflowRunId: string
  chatId: string
}

export interface TriggerConnectorRef {
  alias: string
  connectorType: string
  status: string
}
